/**
 * Factory for converting JSON schema to tree items and back
 */

// Converts JSON schema to tree items for the TreeView component
export function schemaToTreeItems(schema) {
  if (!schema) return [];

  const convertSchemaToTreeItems = (schema, path = '') => {
    if (!schema) return [];

    const items = [];

    if (schema.type === 'object' && schema.properties) {
      Object.entries(schema.properties).forEach(([key, prop]) => {
        const currentPath = path ? `${path}.${key}` : key;
        const isEnum = prop.enum && Array.isArray(prop.enum) && prop.enum.length > 0;

        const item = {
          id: currentPath,
          name: key,
          type: isEnum ? 'enum' : prop.type,
          description: prop.description || '',
          required: schema.required?.includes(key) || false,
          children: [],
          enum: isEnum ? [...prop.enum] : undefined
        };

        if (prop.type === 'object' && prop.properties) {
          item.children = convertSchemaToTreeItems(prop, currentPath);
        } else if (prop.type === 'array' && prop.items) {
          if (Array.isArray(prop.items)) {
            item.children = prop.items.flatMap((subItem, index) =>
              convertSchemaToTreeItems(subItem, `${currentPath}[${index}]`)
            );
          } else {
            item.children = convertSchemaToTreeItems(prop.items, `${currentPath}[]`);
          }
        }

        items.push(item);
      });
    } else if (schema.type === 'array' && schema.items) {
      if (Array.isArray(schema.items)) {
        const arrayItems = schema.items.flatMap((subItem, index) =>
          convertSchemaToTreeItems(subItem, `${path}[${index}]`)
        );
        items.push(...arrayItems);
      } else {
        const arrayItems = convertSchemaToTreeItems(schema.items, `${path}[]`);
        items.push(...arrayItems);
      }
    }

    return items;
  };

  return convertSchemaToTreeItems(schema);
}

// Converts tree items back to JSON schema
export function treeItemsToSchema(items) {
  if (!items || !items.length) return { type: 'object', properties: {} };

  const schema = {
    type: 'object',
    properties: {},
    required: []
  };

  buildSchemaFromItems(items, schema);

  if (!schema.required.length) {
    delete schema.required;
  }

  return schema;
}

function buildSchemaFromItems(items, parentSchema) {
  items.forEach((item) => {
    const key = item.name;

    const property = {};

    // Handle enum type differently
    if (item.type === 'enum') {
      property.type = 'string';
      if (item.enum && Array.isArray(item.enum) && item.enum.length > 0) {
        property.enum = [...item.enum];
      }
    } else {
      property.type = item.type;
    }

    if (item.description) {
      property.description = item.description;
    }

    if (item.required) {
      if (!parentSchema.required) {
        parentSchema.required = [];
      }
      parentSchema.required.push(key);
    }

    if (item.type === 'object') {
      property.properties = {};
      if (item.children && item.children.length) {
        buildSchemaFromItems(item.children, property);
      }
    } else if (item.type === 'array') {
      property.items = { type: 'object', properties: {} };
      if (item.children && item.children.length) {
        buildSchemaFromItems(item.children, property.items);
      }
    }

    parentSchema.properties[key] = property;
  });
}
